import styled, { css } from 'styled-components';

import { AutoComplete } from '@xing-com/auto-complete';
import { Button } from '@xing-com/button';
import {
  mediaSmallOrTiny,
  mediaTopBarHidden,
  mediaTopBarVisible,
} from '@xing-com/layout-tokens';
import {
  scale100,
  scale080,
  xdlColorSliderTickMark,
  cornerRadiusXL,
  xdlColorBackground,
  xdlColorBackgroundTertiary,
  spaceM,
} from '@xing-com/tokens';

export const ERROR_BANNER_HEIGHT_VARIABLE = '--error-banner-height';

const TEXTAREA_2_LINES_HEIGHT = 72;
const TEXTAREA_5_LINES_HEIGHT = 144;
const TEXTAREA_3_LINES_HEIGHT_LG = 132;
const TEXTAREA_6_LINES_HEIGHT_LG = 222;

const DEFAULT_HEIGHT_CONSTRAINTS = {
  TOP_BAR_VISIBLE: {
    MIN_HEIGHT: TEXTAREA_2_LINES_HEIGHT,
    MAX_HEIGHT: TEXTAREA_2_LINES_HEIGHT,
  },
  TOP_BAR_HIDDEN: {
    MIN_HEIGHT: TEXTAREA_3_LINES_HEIGHT_LG,
    MAX_HEIGHT: TEXTAREA_3_LINES_HEIGHT_LG,
  },
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const CSS_HEIGHT_CONSTRAINTS = {
  TOP_BAR_VISIBLE: css`
    min-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MIN_HEIGHT}px;
    max-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MAX_HEIGHT}px;
  `,
  TOP_BAR_HIDDEN: css`
    min-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_HIDDEN.MIN_HEIGHT}px;
    max-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_HIDDEN.MAX_HEIGHT}px;
  `,
};

const withErrorStyles = css`
  padding-bottom: calc(var(${ERROR_BANNER_HEIGHT_VARIABLE}) + 20px);
  scroll-padding-bottom: calc(var(${ERROR_BANNER_HEIGHT_VARIABLE}) + 20px);

  @media ${mediaTopBarVisible} {
    min-height: calc(
      ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MIN_HEIGHT}px +
        var(${ERROR_BANNER_HEIGHT_VARIABLE})
    );
    max-height: calc(
      ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MAX_HEIGHT}px +
        var(${ERROR_BANNER_HEIGHT_VARIABLE})
    );
  }

  @media ${mediaTopBarHidden} {
    min-height: calc(
      ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MIN_HEIGHT}px +
        var(${ERROR_BANNER_HEIGHT_VARIABLE})
    );
    max-height: calc(
      ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_HIDDEN.MAX_HEIGHT}px +
        var(${ERROR_BANNER_HEIGHT_VARIABLE})
    );
  }
`;

export const Input = styled(AutoComplete)<{ $withErrorBanner?: boolean }>`
  & textarea {
    border-radius: ${cornerRadiusXL};
    scrollbar-width: thin;
    scrollbar-color: ${xdlColorSliderTickMark} transparent;
    /* FIXME: This transition is causing the onBlur text truncation to fail */
    /* transition: max-height 0.2s ease-in-out; */

    ${({ $withErrorBanner }) => ($withErrorBanner ? withErrorStyles : '')}
  }

  & ul {
    @media ${mediaSmallOrTiny} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  @media ${mediaTopBarVisible} {
    & textarea {
      ${CSS_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE}
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      &:focus {
        max-height: ${TEXTAREA_5_LINES_HEIGHT}px;
      }

      &:not(:focus) {
        overflow: hidden;
      }

      &,
      &::placeholder {
        line-height: 1.5;
        font-size: ${scale080};
      }
    }
  }

  @media ${mediaTopBarHidden} {
    & textarea {
      ${CSS_HEIGHT_CONSTRAINTS.TOP_BAR_HIDDEN}

      &:focus {
        max-height: ${TEXTAREA_6_LINES_HEIGHT_LG}px;
      }

      &:not(:focus) {
        overflow: hidden;
      }

      &,
      &::placeholder {
        line-height: 1.5;
        font-size: ${scale100};
      }
    }
  }
`;

export const Container = styled.div`
  margin-top: auto;
  grid-area: key;
  position: relative;
  width: 100%;

  @media ${mediaTopBarVisible} {
    min-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_VISIBLE.MIN_HEIGHT}px;
  }

  @media ${mediaTopBarHidden} {
    min-height: ${DEFAULT_HEIGHT_CONSTRAINTS.TOP_BAR_HIDDEN.MIN_HEIGHT}px;
  }
`;

/**
 * Once issue with the bottom banner is that, since it does not go to the bottom of the
 * autocomplete, if the user scrolls to the top and there's enough text to go under the banner
 * it will be displayed, where there is space between the bottom and the bottom of the banner.
 * This is a workaround to fill the space with the background color.
 */
export const ErrorBannerContainer = styled.div`
  position: absolute;
  bottom: 2px;
  left: 20px;
  right: 20px;
  padding-bottom: 18px;
  background-color: ${xdlColorBackground};
  border-top-left-radius: ${cornerRadiusXL};
  border-top-right-radius: ${cornerRadiusXL};
`;

export const ErrorBanner = styled.div`
  background-color: ${xdlColorBackgroundTertiary};
  padding: ${spaceM};
  padding-right: 42px;
  border-radius: ${cornerRadiusXL};
`;

export const ErrorClose = styled(Button)`
  position: absolute;
  right: 0;
  top: 0;
`;
